import { useTranslation } from 'react-i18next';
import { Loader2, ShieldCheck } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import { Button } from 'components/ui/button';

type TelegramDisconnectConfirmModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => Promise<void>;
  isLoading?: boolean;
  description?: string;
};

export const TelegramDisconnectConfirmModal = ({
  open,
  onOpenChange,
  onConfirm,
  isLoading = false,
  description,
}: TelegramDisconnectConfirmModalProps) => {
  const { t } = useTranslation();

  const handleConfirm = async () => {
    try {
      await onConfirm();
    } catch {
      // Keep the modal open so the user can retry after an error toast.
    }
  };

  const handleCancel = () => {
    if (isLoading) return;
    onOpenChange(false);
  };

  return (
    <AlertDialog open={open} onOpenChange={(nextOpen) => !isLoading && onOpenChange(nextOpen)}>
      <AlertDialogContent
        nested
        className="max-w-[400px] [&>button.absolute]:hidden"
      >
        <AlertDialogHeader className="items-center space-y-4 px-6 pb-2 pt-6 text-center sm:text-center">
          <div className="flex size-14 items-center justify-center rounded-full border border-[#FECACA] bg-[#FEF2F2] dark:border-red-500/30 dark:bg-red-500/10">
            <ShieldCheck
              className="size-6 text-[#DC2626] dark:text-red-400"
              aria-hidden
            />
          </div>
          <AlertDialogTitle className="pr-0 text-xl font-semibold leading-snug">
            {t('TELEGRAM_DISCONNECT_CONFIRM_TITLE', {
              defaultValue: 'Do you really want to disconnect?',
            })}
          </AlertDialogTitle>
          <AlertDialogDescription className="pt-0 text-sm leading-relaxed">
            {description ||
              t('TELEGRAM_DISCONNECT_CONFIRM_DESCRIPTION', {
                defaultValue:
                  'Your Telegram bot will stop responding to chats until you reconnect it.',
              })}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter className="flex-row justify-center gap-3 px-6 pb-6 pt-2 sm:justify-center">
          <Button
            type="button"
            variant="outline"
            className="h-11 min-w-[120px] flex-1 rounded-lg border-[#D9DBE9] bg-white text-base font-medium shadow-none hover:bg-grey-50 dark:border-white/15 dark:bg-transparent dark:hover:bg-white/5"
            disabled={isLoading}
            onClick={handleCancel}
          >
            {t('NO', { defaultValue: 'No' })}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="h-11 min-w-[120px] flex-1 rounded-lg border-[#DC2626] bg-[#DC2626] text-base font-medium text-white shadow-none hover:bg-[#B91C1C] hover:text-white dark:border-red-500 dark:bg-red-600 dark:hover:bg-red-700"
            disabled={isLoading}
            onClick={handleConfirm}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : null}
            {t('YES_DISCONNECT', { defaultValue: 'Yes, Disconnect' })}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
